'use client';

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ReactPlayer from 'react-player';
import { css } from 'styled-system/css';

import ShaojiangImage from '@shaojiang/components/ShaojiangImage';
import { Button } from '@shaojiang/components/ui/button';
import { Carousel } from '@shaojiang/components/ui/carousel';
import { Feature } from '@shaojiang/types/profile';

interface Props {
  features: Feature[];
}

/** Swipeable feature slides (image or mp4) with back/next controls and a step counter. */
export default function ShaojiangCarousel({ features }: Props) {
  const [page, setPage] = useState(0);
  const count = features.length;

  if (count === 0) return null;

  return (
    <Carousel.Root
      slideCount={count}
      page={page}
      onPageChange={(details) => setPage(details.page)}
      allowMouseDrag
      className={css({
        bgImage: "url('/icons/bg-lines.svg')",
        bgPosition: 'center bottom 128px',
        bgRepeat: 'no-repeat',
        gap: '0',
      })}
    >
      <Carousel.ItemGroup>
        {features.map((feature, index) => (
          <Carousel.Item key={feature.name} index={index} className={css({ p: '4', pr: '3' })}>
            {feature.url.endsWith('.mp4') ? (
              <ReactPlayer src={feature.url} controls width='100%' height={432} />
            ) : (
              <ShaojiangImage
                src={feature.url}
                alt={feature.name}
                width={768}
                height={432}
                className={css({
                  display: 'block',
                  mx: 'auto',
                  boxShadow: 'md',
                  maxW: 'full',
                  h: 'auto',
                })}
              />
            )}
            <h5 className={css({ mt: '4', mb: '2' })}>Feature: {feature.name}</h5>
            <p>{feature.description}</p>
          </Carousel.Item>
        ))}
      </Carousel.ItemGroup>
      <Carousel.Control
        className={css({
          position: 'static',
          transform: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mx: '2',
          px: '2',
          py: '2',
          bg: 'bg.primaryMuted',
          borderRadius: 'l2',
        })}
      >
        <Carousel.PrevTrigger asChild>
          <Button size='sm'>
            <ChevronLeft />
            Back
          </Button>
        </Carousel.PrevTrigger>
        <span className={css({ textStyle: 'sm' })}>
          {page + 1} / {count}
        </span>
        <Carousel.NextTrigger asChild>
          <Button size='sm'>
            Next
            <ChevronRight />
          </Button>
        </Carousel.NextTrigger>
      </Carousel.Control>
    </Carousel.Root>
  );
}
